import { useEffect, useRef, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, Loader2, SearchX } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { cn } from '@/utils/cn'
import type { WavePlanDetail } from '@/api/wavePlans'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { errorStatus } from '@/hooks/useWavePlans'
import { WavePlanningNav } from './WavePlanningNav'
import { WavePlansBackLink } from './WavePlansBackLink'
import { WavePlanWorkflowBar } from './WavePlanWorkflowBar'

export function WavePlanLoading({
  label = 'Loading wave plan…',
}: {
  label?: string
}) {
  return (
    <div
      role="status"
      className="flex items-center gap-2 p-10 text-sm text-muted-foreground"
    >
      <Loader2 size={16} className="animate-spin" /> {label}
    </div>
  )
}

/**
 * A failed load: "not available" for a missing or forbidden plan, otherwise an
 * error with a retry (the request toast already gave the details). The rollup
 * passes its own wording.
 */
export function WavePlanLoadError({
  error,
  onRetry,
  unavailable = {
    title: 'Wave plan not available',
    message: 'It may not exist or you may not have access to it.',
  },
  failedTitle = 'The wave plan could not be loaded',
  className,
}: {
  error: unknown
  onRetry: () => void
  unavailable?: { title: string; message: string }
  failedTitle?: string
  className?: string
}) {
  const status = errorStatus(error)
  const missing = status === 404 || status === 403 || status === 422
  return (
    <Card
      className={cn(
        'm-6 flex flex-col items-center gap-3 p-12 text-center shadow-none',
        className
      )}
    >
      {missing ? (
        <SearchX size={28} className="text-muted-foreground" />
      ) : (
        <AlertTriangle size={28} className="text-destructive" />
      )}
      <h2 className="text-lg font-semibold">
        {missing ? unavailable.title : failedTitle}
      </h2>
      <p className="max-w-md text-sm text-muted-foreground">
        {missing ? unavailable.message : 'Check your connection and try again.'}
      </p>
      <div className="flex gap-2">
        {!missing && <Button onClick={onRetry}>Try again</Button>}
        <Button variant={missing ? 'default' : 'outline'} asChild>
          <Link to="/wave-planning">Back to plans</Link>
        </Button>
      </div>
    </Card>
  )
}

/** Header, view navigation and workflow bar shared by the plan views. */
export function WavePlanPageShell({
  detail,
  title,
  subtitle,
  compact = false,
  children,
}: {
  detail: WavePlanDetail
  title: string
  subtitle?: ReactNode
  compact?: boolean
  children: ReactNode
}) {
  const heading = useRef<HTMLHeadingElement>(null)
  useDocumentTitle(title, detail.assortment_plan_name)
  useEffect(() => {
    // Switching views remounts the page; keep keyboard focus in the content.
    if (document.activeElement === document.body) heading.current?.focus()
  }, [title])
  return (
    <div
      className={`flex min-w-0 flex-col ${compact ? 'gap-1.5 px-4 py-3' : 'gap-4 p-4 md:p-6'}`}
    >
      <header className="flex flex-col gap-2">
        <div className="min-w-0">
          <WavePlansBackLink />
          <h1
            ref={heading}
            tabIndex={-1}
            className={`outline-none ${compact ? 'text-xl font-semibold' : 'text-2xl font-semibold'}`}
          >
            {title}
          </h1>
          <p className="mt-1 text-xs break-words text-muted-foreground">
            <span className="font-medium text-foreground">
              {detail.assortment_plan_name}
            </span>
            {subtitle && <span className="mx-2">·</span>}
            {subtitle}
          </p>
        </div>
        <WavePlanWorkflowBar detail={detail} />
      </header>
      <WavePlanningNav planId={detail.id} compact={compact} />
      {children}
    </div>
  )
}
